import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.js';
import { ProtectedRoute } from './components/common/ProtectedRoute.js';
import { Layout } from './components/common/Layout.js';
import { LoginPage } from './pages/login/login.js';
import { HomePage } from './pages/home/home.js';
import { MembersPage } from './pages/members/members.js';
import { RolesPage } from './pages/roles/roles.js';
import { PipelinePage } from './pages/pipeline/pipeline.js';
import { CandidateDetailPage } from './pages/candidate-detail/candidate-detail.js';
import { ProfilePage } from './pages/profile/profile.js';
import { MissionControlPage } from './pages/mission-control/mission-control.js';
import { FacilitySelectPage } from './pages/insights/facility-select.js';
import { InsightsPage } from './pages/insights/insights.js';
import { CommandCenterPage } from './pages/command-center/command-center.js';
import { CanopusFacilitySelectPage } from './pages/canopus-command/facility-select.js';
import { CanopusCommandPage } from './pages/canopus-command/canopus-command.js';
import { SignupPage } from './pages/signup/signup.js';
import { NotificationSettingsPage } from './pages/notification-settings/notification-settings.js';
import { BrandBookPage } from './pages/brand-book/brand-book.js';
import { NOTIF_CONFIG_ROLES, PAGE_VIEW_ROLES } from '@onboarding-dashboard/shared';

/** Redirect old /register?token=xxx links to /signup?token=xxx */
function RegisterRedirect(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const target = token ? `/signup?token=${encodeURIComponent(token)}` : '/login';
  return <Navigate to={target} replace />;
}

function App(): React.ReactElement {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/register" element={<RegisterRedirect />} />
          <Route
            path="/members"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.members}>
                <Layout>
                  <MembersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute roles={['admin', 'hr', 'manager', 'employee']}>
                <Layout>
                  <RolesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline"
            element={
              <ProtectedRoute roles={['admin', 'hr', 'manager', 'employee']}>
                <Layout>
                  <PipelinePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidates/:id"
            element={
              <ProtectedRoute roles={['admin', 'hr', 'manager', 'employee']}>
                <Layout>
                  <CandidateDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mission-control"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.missionControl}>
                <Layout>
                  <MissionControlPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.insights}>
                <Layout>
                  <FacilitySelectPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/:facilityId"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.insights}>
                <Layout>
                  <InsightsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/command-center"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.commandCenter}>
                <Layout>
                  <CommandCenterPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/canopus-command"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.canopusCommand}>
                <Layout>
                  <CanopusFacilitySelectPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/canopus-command/:facilityId"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.canopusCommand}>
                <Layout>
                  <CanopusCommandPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline/notification-settings"
            element={
              <ProtectedRoute roles={[...NOTIF_CONFIG_ROLES]}>
                <Layout>
                  <NotificationSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-book"
            element={
              <ProtectedRoute roles={PAGE_VIEW_ROLES.brandBook}>
                <Layout>
                  <BrandBookPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <HomePage />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
